import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Divider, IconButton, Paper, Stack, Tooltip, Typography} from '@mui/material';
import {Check, Edit} from '@mui/icons-material';
import {DndContext, DragEndEvent, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors,} from '@dnd-kit/core';
import CNumberField from 'crqlar-react-common/dist/components/CNumberField';
import {EdgeSeats, SeatSlotWithPosition} from '../types';
import {FABRIC_DEFAULTS} from '../utils/fabric-defaults';
import {TABLE_CAPACITY_MAX} from '../utils/constants';
import {buildDisplayNumberById} from '../utils/table-configurator-utils';
import TableSettingsSeatPriorityDraggable from './TableSettingsSeatPriorityDraggable';

interface Props {
    width: number;
    height: number;
    seatOffset: number;
    tableName: string;
    edges: EdgeSeats;
    visualSlots: SeatSlotWithPosition[];
    onUpdateEdgeSeats: (edge: keyof EdgeSeats, count: number) => void;
    onSwapSeatPriorities: (fromId: string, toId: string) => void;
}

const TableSettingsSeatRectConfigurator: React.FC<Props> = ({
                                                                width,
                                                                height,
                                                                seatOffset,
                                                                tableName,
                                                                edges,
                                                                visualSlots,
                                                                onUpdateEdgeSeats,
                                                                onSwapSeatPriorities,
                                                            }) => {
    const {t} = useTranslation();
    const [editMode, setEditMode] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    const displayNumberById = useMemo(() => buildDisplayNumberById(visualSlots), [visualSlots]);
    const seatCount = visualSlots.length;
    const isEditingDisabled = visualSlots.length < 2;
    const padding = seatOffset + 20;
    const svgWidth = width + padding * 2;
    const svgHeight = height + padding * 2;
    const centerX = svgWidth / 2;
    const centerY = svgHeight / 2;

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
        useSensor(TouchSensor, {activationConstraint: {delay: 150, tolerance: 8}})
    );

    const activeDisplayNumber = useMemo(
        () => (activeId ? (displayNumberById.get(activeId) ?? 1) : 1),
        [activeId, displayNumberById]
    );

    return (
        <>
            <Typography variant="subtitle2" gutterBottom>
                {t('Seat Configuration')}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    p: 1,
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#fafafa',
                    position: 'relative',
                    borderColor: editMode ? 'warning.main' : undefined,
                    borderWidth: editMode ? 2 : 1,
                    transition: 'border-color 200ms ease',
                }}
            >
                <DndContext
                    sensors={sensors}
                    onDragStart={(event) => {
                        setActiveId(event.active.id as string);
                        setOverId(null);
                    }}
                    onDragEnd={(event: DragEndEvent) => {
                        const fromId = event.active.id as string;
                        const toId = (event.over?.id as string) ?? null;

                        setActiveId(null);
                        setOverId(null);

                        if (!toId || fromId === toId) return;
                        onSwapSeatPriorities(fromId, toId);
                    }}
                    onDragOver={(event) => setOverId((event.over?.id as string) ?? null)}
                    onDragCancel={() => {
                        setActiveId(null);
                        setOverId(null);
                    }}
                >
                    <svg
                        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                        style={{
                            width: '100%',
                            maxWidth: 220,
                            maxHeight: 180,
                            touchAction: editMode ? 'none' : 'auto',
                            overflow: 'visible',
                            userSelect: 'none',
                        }}
                    >
                        <rect
                            x={centerX - width / 2}
                            y={centerY - height / 2}
                            width={width}
                            height={height}
                            fill={FABRIC_DEFAULTS.fill}
                            stroke={FABRIC_DEFAULTS.stroke}
                            strokeWidth={2}
                            rx={4}
                        />

                        {visualSlots.map((slot) => (
                            <TableSettingsSeatPriorityDraggable
                                key={slot.id}
                                slot={slot}
                                cx={centerX + slot.x}
                                cy={centerY + slot.y}
                                editMode={editMode}
                                isDragging={activeId === slot.id}
                                isOver={overId === slot.id}
                                displayNumber={displayNumberById.get(slot.id) ?? (slot.priority ?? slot.position) + 1}
                            />
                        ))}

                        <text
                            x={centerX}
                            y={centerY - 6}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={12}
                            fontWeight="bold"
                            fill={FABRIC_DEFAULTS.table.textColor}
                            style={{pointerEvents: 'none', userSelect: 'none'}}
                        >
                            {tableName}
                        </text>
                        <text
                            x={centerX}
                            y={centerY + 8}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={10}
                            fontWeight="normal"
                            fill={FABRIC_DEFAULTS.table.textColor}
                            style={{pointerEvents: 'none', userSelect: 'none'}}
                        >
                            ({seatCount})
                        </text>
                    </svg>

                    <DragOverlay dropAnimation={null}>
                        {activeId && (
                            <svg width={34} height={34} viewBox="0 0 34 34" style={{display: 'block'}}>
                                <circle
                                    cx={17}
                                    cy={17}
                                    r={12}
                                    fill={FABRIC_DEFAULTS.seat.fillActive}
                                    stroke="#1976d2"
                                    strokeWidth={3}
                                />
                                <text
                                    x={17}
                                    y={17}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={11}
                                    fontWeight="bold"
                                    fill="#fff"
                                    style={{pointerEvents: 'none', userSelect: 'none'}}
                                >
                                    {activeDisplayNumber}
                                </text>
                            </svg>
                        )}
                    </DragOverlay>
                </DndContext>

                <Tooltip
                    title={editMode ? t('Done reordering') : t('Reorder seat priorities')}
                    placement="left"
                >
                    <span style={{position: 'absolute', top: 4, right: 4}}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setActiveId(null);
                                setOverId(null);
                                setEditMode((value) => !value);
                            }}
                            disabled={isEditingDisabled}
                            color={editMode ? 'warning' : 'default'}
                            sx={{
                                bgcolor: 'background.paper',
                                transition: 'background-color 200ms ease',
                                '&:hover': {bgcolor: 'action.hover'},
                            }}
                        >
                            {editMode ? <Check fontSize="small"/> : <Edit fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
            </Paper>

            <Typography variant="body2" color={editMode ? 'warning.main' : 'text.secondary'} sx={{mb: 2}}>
                {editMode
                    ? t('Drag a seat and drop it on another to swap their priorities. Lower numbers are filled first.')
                    : t('Numbers indicate booking priority. Seat 1 is filled first when guests book.')}
            </Typography>

            <Divider sx={{mb: 2}}/>
            <Typography variant="subtitle2" sx={{mb: 1.5}}>
                {t('Seats per Edge')}
            </Typography>
            <Stack spacing={1.5}>
                {([
                    ['top', t('Top')],
                    ['right', t('Right')],
                    ['bottom', t('Bottom')],
                    ['left', t('Left')],
                ] as const).map(([edge, label]) => {
                    const maxByEdge = TABLE_CAPACITY_MAX - (
                        edges.top + edges.right + edges.bottom + edges.left - edges[edge]
                    );

                    return (
                        <Stack key={edge} direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" sx={{width: 60}}>
                                {label}
                            </Typography>
                            <CNumberField
                                size="small"
                                value={edges[edge]}
                                onChange={(value: number | null) => value !== null && value >= 0 && onUpdateEdgeSeats(edge, value)}
                                min={0}
                                max={maxByEdge}
                                sx={{flex: 1}}
                            />
                        </Stack>
                    );
                })}
            </Stack>
        </>
    );
};

export default TableSettingsSeatRectConfigurator;
